<template>
	<div class="outer-wrap">
		<CTabs
			v-if="isShowTab"
			class="c-tab"
			v-model="activeIndex"
			:option="tabOption"
			:ellipsis="false"
			@change="changeActive"
		/>
		<MStateBox
			:loading="custom.loading"
			:empty="isEmpty"
			:class="`custom-wrap ${isShowTab ? 'show-tab-wrap' : ''}`"
		>
			<van-pull-refresh v-model="isLoading" success-text="刷新成功" @refresh="onPullDown">
				<div class="item-wrap" v-if="finalData.length">
					<div
						class="report-wrapper"
						v-for="(item, index) in finalData"
						:key="item.fileUrl || item.subject || index"
						@click="handleClick(item)"
					>
						<img v-if="isPdfFile(item)" class="report-cover" :src="covers[item.fileUrl]" alt="" />
						<img v-else class="report-cover" :src="item.logoUrl" alt="" />
						<p class="report-title">{{ item.subject }}</p>
					</div>
				</div>
			</van-pull-refresh>
		</MStateBox>
	</div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { usePaginationMobile } from '@rebuild/hooks/load-more/usePaginationMobile';
import { useModuleNavigation } from '@rebuild/hooks/useModuleNavigation';
import CTabs from '@/view/enterprise/components/c-tabs.vue';
import MStateBox from '@/view/enterprise_rebuild/components/MStateBox.vue';
import { PullRefresh as VanPullRefresh } from 'vant';
import { useCustomCore } from '@rebuild/materials/shared/custom/context';
import i18n, { EN, ZH_HK } from '@/assets/js/i18n';
import { getPDFCover } from '@/utils/api';

const { custom } = useModuleData();
const { viewModule } = useModuleNavigation();

const activeTab = ref(i18n.locale === ZH_HK ? '年度報告' : '年度报告');
const activeIndex = ref(0);

const tabOption = computed(() => {
	if (i18n.locale === ZH_HK) {
		return ['年度報告', '中期報告'];
	} else {
		return ['年度报告', '中期报告'];
	}
});

const isPdfFile = item => {
	const { fileUrl, type } = item;
	if (type != 1) return false;
	return fileUrl.endsWith('.pdf');
};

const isLoading = ref(false);
// 按 fileUrl 缓存 PDF 封面，先出列表再异步更新，避免阻塞首屏
const covers = ref({});
// 接口拉取的全量列表（未按 tab 筛选），切换 tab 时只做本地筛选，不再请求接口
const fullListRef = ref([]);

const isEn = computed(() => {
	return i18n.locale === EN;
});
const { getRealModuleId } = useCustomCore();
const getModuleId = computed(() => {
	return getRealModuleId();
});

const isShowTab = computed(() => {
	return !isEn.value;
});

// 空状态判断
const isEmpty = computed(() => {
	return custom.empty || !finalData.value?.length;
});

const { findModule } = useCustomCore();
const handleClick = item => {
	const findModuleData = findModule(getModuleId.value);
	viewModule('custom', item, findModuleData.name);
};

// 按当前 tab 从全量列表中筛选
const filterByActiveTab = rows => {
	if (!isShowTab.value) return rows;
	if (activeTab.value.includes('年度')) {
		return rows.filter(item => {
			const subject = item.subject || '';
			return subject.includes('年度') || subject.includes('Annual');
		});
	}
	if (activeTab.value.includes('中期')) {
		return rows.filter(item => {
			const subject = item.subject || '';
			return subject.includes('中期') || subject.includes('Interim');
		});
	}
	return rows;
};

// 仅对给定列表按每 3 组一批拉取 PDF 封面（当前 tab 用，切换 tab 时再处理该 tab 数据）
const BATCH_SIZE = 3;
const fetchCoversForList = list => {
	(async () => {
		const needFetch = (list || []).filter(item => item.fileUrl && !covers.value[item.fileUrl]);
		for (let i = 0; i < needFetch.length; i += BATCH_SIZE) {
			const chunk = needFetch.slice(i, i + BATCH_SIZE);
			const results = await Promise.all(
				chunk.map(async item => {
					try {
						const cover = await getPDFCover(item.fileUrl);
						return { fileUrl: item.fileUrl, cover };
					} catch (_) {
						return { fileUrl: item.fileUrl, cover: '' };
					}
				})
			);
			const updates = {};
			results.forEach(({ fileUrl, cover }) => {
				if (cover) updates[fileUrl] = cover;
			});
			if (Object.keys(updates).length) {
				covers.value = Object.assign({}, covers.value, updates);
			}
		}
	})();
};

const changeActive = idx => {
	activeIndex.value = idx;
	activeTab.value = tabOption.value[idx];
	// 只做本地筛选，不重新请求接口
	const filtered = filterByActiveTab(fullListRef.value);
	setData(filtered, filtered.length);
	// 切换 tab 后只处理当前 tab 的封面
	fetchCoversForList(filtered);
};

// 数据加载
const loadData = async () => {
	const params = {
		moduleId: getModuleId.value,
		pageStart: currentPage.value - 1,
		pageNum: 1000
	};

	await custom.refresh(params);

	const data = custom.data;
	const rows = data?.rows || [];
	fullListRef.value = rows;

	const newData = filterByActiveTab(rows);
	const total = newData.length;

	setData(newData, total);

	// 只处理当前 tab 的数据，逐个拉取封面
	fetchCoversForList(newData);
};

// 统一移动端分页处理
const {
	currentPage,
	finalData,
	setData,
	onPullDown: paginationOnPullDown,
	changeCurrentPage
} = usePaginationMobile({
	bindChange: loadData
});

// 处理 van-pull-refresh 的下拉刷新
const onPullDown = async () => {
	await paginationOnPullDown(() => {
		isLoading.value = false;
	});
};

onMounted(async () => {
	await loadData();
});
</script>

<style lang="less" scoped>
.outer-wrap {
	height: 100%;
	.c-tab {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		z-index: 100;
	}
}
.custom-wrap {
	height: 100%;
	background: #fff;
	overflow-y: auto;
	/deep/.van-pull-refresh {
		min-height: 100%;
	}
	.item-wrap {
		display: flex;
		flex-wrap: wrap;
		justify-content: left;
		gap: 0.16rem;
		width: fit-content;
		max-width: 100%;
		margin: 0 auto;
		padding: 0.14rem 0 0;
		.report-wrapper {
			width: 1.2rem;
			.report-cover {
				width: 100%;
				height: 1.7rem;
				border: 1px solid var(--border-lighter-color);
				background-color: #fff;
			}
			.report-title {
				margin-top: 0.12rem;
				color: #333;
				font-size: 0.14rem;
				font-style: normal;
				font-weight: 500;
				line-height: 0.18rem;
			}
		}
	}
	.empty-box {
		height: 100%;
	}
}
.show-tab-wrap {
	padding-top: 0.42rem;
}

/* 窄于 3 列总宽时改为 2 列，整块居中（媒体查询用 px，rem 会按浏览器 16px 算约 63px 几乎不触发） */
@media (max-width: 432px) {
	.custom-wrap .item-wrap {
		width: 2.6rem;
		margin: 0 auto;
	}
}
</style>
